import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { useAuth } from "../../js/hooks/useAuth";
import {
  EVENT_SOURCES,
  addCustomHubRoom,
  blockHubUser,
  buildFounderSnapshot,
  decideEventRequest,
  ensureFounderHub,
  ensureTheHub,
  forceRefreshHubFeeds,
  getFounderSettings,
  hideHubEvent,
  isFounderNight,
  lastHubFeedDay,
  listEventRequests,
  listHubBlocks,
  listHubEvents,
  listHubGroups,
  listHubPosts,
  listHubVisits,
  listHubWarnings,
  patchHubRoom,
  removeHubRoom,
  saveHubEvent,
  setFounderNight,
  setFounderSettings,
  unblockHubUser,
  useTheHubLive,
  warnHubMember,
} from "../../js/theHub";
import HubListingEditModal from "../../components/theHub/HubListingEditModal";

function maxOf(rows, key = "count") {
  return Math.max(1, ...rows.map((row) => Number(row[key] || 0)));
}

function RhythmChart({ series, labels }) {
  const width = 420;
  const height = 128;
  const pad = 18;
  const peak = maxOf(series);
  const points = series.map((row, index) => {
    const x = pad + (index / Math.max(1, series.length - 1)) * (width - pad * 2);
    const y = height - pad - (Number(row.count || 0) / peak) * (height - pad * 2);
    return `${x},${y}`;
  });
  const fill = `${pad},${height - pad} ${points.join(" ")} ${width - pad},${height - pad}`;
  return (
    <svg className="founders-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-hidden="true">
      <polygon points={fill} className="founders-chart__wash" />
      <polyline points={points.join(" ")} className="founders-chart__line" />
      {labels
        ? labels.map((label, index) => {
            const x = pad + (index / Math.max(1, labels.length - 1)) * (width - pad * 2);
            return (
              <text key={label} x={x} y={height - 4} className="founders-chart__tick">
                {label}
              </text>
            );
          })
        : null}
    </svg>
  );
}

function MovementGraph({ flows }) {
  const width = 640;
  const height = 260;
  const left = [...new Set(flows.map((row) => row.from))];
  const right = [...new Set(flows.map((row) => row.to))];
  const peak = maxOf(flows);
  const yFor = (list, id) => {
    const index = list.indexOf(id);
    return 28 + (index / Math.max(1, list.length - 1)) * (height - 56);
  };
  return (
    <svg className="founders-sankey" viewBox={`0 0 ${width} ${height}`} role="img" aria-hidden="true">
      {flows.map((row) => {
        const y1 = yFor(left, row.from);
        const y2 = yFor(right, row.to);
        const mid = width / 2;
        const widthStroke = 2 + (row.count / peak) * 14;
        return (
          <path
            key={`${row.from}-${row.to}`}
            d={`M 150 ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${width - 150} ${y2}`}
            className="founders-sankey__flow"
            style={{ strokeWidth: widthStroke }}
          />
        );
      })}
      {left.map((id) => {
        const row = flows.find((item) => item.from === id);
        return (
          <text key={`l-${id}`} x="8" y={yFor(left, id) + 4} className="founders-sankey__label">
            {row?.fromLabel || id}
          </text>
        );
      })}
      {right.map((id) => {
        const row = flows.find((item) => item.to === id);
        return (
          <text key={`r-${id}`} x={width - 142} y={yFor(right, id) + 4} className="founders-sankey__label">
            {row?.toLabel || id}
          </text>
        );
      })}
    </svg>
  );
}

function Meter({ label, value, peak }) {
  const width = Math.round((Number(value || 0) / Math.max(1, peak)) * 100);
  return (
    <div className="founders-meter">
      <span>
        {label} <em>{value}</em>
      </span>
      <i style={{ width: `${width}%` }} />
    </div>
  );
}

export default function FoundersView() {
  const { session, role } = useAuth();
  const [pulse, setPulse] = useState(0);
  const [heatAt, setHeatAt] = useState(Date.now());
  const [night, setNight] = useState(() => isFounderNight());
  const [roomName, setRoomName] = useState("");
  const [roomBlurb, setRoomBlurb] = useState("");
  const [renameId, setRenameId] = useState("");
  const [renameLabel, setRenameLabel] = useState("");
  const [watchUser, setWatchUser] = useState("");
  const [warnNote, setWarnNote] = useState("");
  const [note, setNote] = useState("");
  const [urlEditItem, setUrlEditItem] = useState(null);
  const [intervalHours, setIntervalHours] = useState(() => getFounderSettings().intervalHours || 24);

  ensureTheHub();
  ensureFounderHub();
  useTheHubLive(() => setPulse((n) => n + 1));

  useEffect(() => {
    const heat = setInterval(() => setHeatAt(Date.now()), 60 * 1000);
    const feed = setInterval(() => setPulse((n) => n + 1), 30 * 1000);
    return () => {
      clearInterval(heat);
      clearInterval(feed);
    };
  }, []);

  const posts = useMemo(() => listHubPosts(), [pulse]);
  const events = useMemo(() => listHubEvents(), [pulse]);
  const requests = useMemo(() => listEventRequests(), [pulse]);
  const groups = useMemo(() => listHubGroups(), [pulse]);
  const visits = useMemo(() => listHubVisits(), [pulse]);
  const blocks = useMemo(() => listHubBlocks(), [pulse]);
  const warnings = useMemo(() => listHubWarnings(), [pulse]);
  const settings = useMemo(() => getFounderSettings(), [pulse]);
  const snapshot = useMemo(
    () => buildFounderSnapshot({ posts, events, requests, groups }),
    [posts, events, requests, groups]
  );
  const feedDay = useMemo(() => lastHubFeedDay(), [pulse]);
  const offSources = new Set(settings.offSources || []);
  const sentimentTotal = Math.max(1, snapshot.sentiment.positive + snapshot.sentiment.neutral + snapshot.sentiment.friction);
  const authors = useMemo(() => {
    const names = new Map();
    posts.forEach((row) => {
      if (row.author_id) names.set(row.author_id, row.author_name || row.author_id);
    });
    visits.forEach((row) => {
      if (row.user_id && !names.has(row.user_id)) names.set(row.user_id, row.user_id);
    });
    return [...names.entries()].map(([id, name]) => ({ id, name }));
  }, [posts, visits]);
  const watched = authors.find((row) => row.id === watchUser) || authors[0] || null;
  const watchedPosts = posts.filter((row) => row.author_id === watched?.id);
  const watchedRooms = visits.filter((row) => row.user_id === watched?.id).slice(-8).reverse();

  function flash(message) {
    setNote(message);
    setPulse((n) => n + 1);
  }

  function toggleNight() {
    const next = !night;
    setFounderNight(next);
    setNight(next);
  }

  function toggleSource(id) {
    const next = new Set(offSources);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFounderSettings({ offSources: [...next] });
    flash("Source list updated. New listings will follow this choice.");
  }

  if (role !== "admin") {
    return (
      <AppLayout variant="admin">
        <p>Founder’s View is for the program desk only.</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout variant="admin">
      <div className={`founders${night ? " founders--night" : ""}`}>
        <header className="founders__top">
          <div>
            <p className="eyebrow">Program desk</p>
            <h1>Founder’s View</h1>
            <p className="page-lead">
              A quiet cockpit for The Hub — rooms, listings, and how members move through the hall.
              Client files stay outside this door.
            </p>
          </div>
          <div className="founders__clocks">
            <p>Heatmap refresh · {new Date(heatAt).toLocaleTimeString()}</p>
            <p>Listings day · {feedDay}</p>
            <button type="button" className="ghost-btn" onClick={toggleNight}>
              {night ? "Woodland day" : "Night cockpit"}
            </button>
            <Link className="primary-btn" to="/admin/hub#hub-listing-editor">
              Hub Review — edit listings
            </Link>
          </div>
        </header>
        {note ? <p className="confirm-banner">{note}</p> : null}

        <div className="founders__grid">
          <section className="founders-panel" aria-labelledby="fv-heat">
            <h2 id="fv-heat">Activity heatmap</h2>
            <p className="muted">Green is healthy. Gray is quiet. Updates about once a minute.</p>
            <ol className="founders-heat">
              {snapshot.heatmap.map((room) => (
                <li key={room.id} className={`founders-heat__cell is-${room.heat.tone}${room.muted ? " is-muted" : ""}`}>
                  <strong>{room.label}</strong>
                  <span>{room.heat.label}</span>
                  <em>{room.score}</em>
                </li>
              ))}
            </ol>
          </section>

          <section className="founders-panel" aria-labelledby="fv-feed">
            <h2 id="fv-feed">Live engagement</h2>
            <p className="muted">Posts, replies, requests, and circle notes. Refreshes on its own.</p>
            <ol className="founders-feed">
              {snapshot.feed.map((row) => (
                <li key={row.id}>
                  <span className={`founders-pill is-${row.kind}`}>{row.kind}</span>
                  <p>{row.text}</p>
                </li>
              ))}
              {!snapshot.feed.length ? <li className="muted">The hall is still this hour.</li> : null}
            </ol>
          </section>

          <section className="founders-panel" aria-labelledby="fv-events">
            <h2 id="fv-events">Event interest</h2>
            <ul className="founders-events">
              {snapshot.interest.map((event) => {
                const peak = Math.max(event.views, event.shares, event.attending, event.interested, 1);
                return (
                  <li key={event.id}>
                    <strong>{event.title}</strong>
                    <Meter label="Interested" value={event.interested} peak={peak} />
                    <Meter label="Attending" value={event.attending} peak={peak} />
                    <Meter label="Views" value={event.views} peak={peak} />
                    <Meter label="Shares" value={event.shares} peak={peak} />
                  </li>
                );
              })}
              {!snapshot.interest.length ? <li className="muted">No listings to measure yet.</li> : null}
            </ul>
          </section>

          <section className="founders-panel founders-panel--wide" aria-labelledby="fv-move">
            <h2 id="fv-move">Cross-room movement</h2>
            <p className="muted">How members step from city to county, then outward — and from circles into gatherings.</p>
            <MovementGraph flows={snapshot.movement} />
            <ul className="founders-flow-legend">
              {snapshot.movement.map((row) => (
                <li key={`${row.from}-${row.to}`}>
                  {row.fromLabel} → {row.toLabel} · {row.count}
                </li>
              ))}
            </ul>
          </section>

          <section className="founders-panel" aria-labelledby="fv-rhythm">
            <h2 id="fv-rhythm">Daily usage rhythm</h2>
            <p className="muted">Hourly visits and notes, then the last seven days.</p>
            <RhythmChart
              series={snapshot.hourly}
              labels={["12a", "6a", "12p", "6p", "11p"]}
            />
            <RhythmChart series={snapshot.weekly} labels={snapshot.weekly.map((row) => row.label)} />
          </section>

          <section className="founders-panel" aria-labelledby="fv-tone">
            <h2 id="fv-tone">Sentiment indicators</h2>
            <p className="muted">
              Pattern only — thanks and “I’ll attend” read as ease; repeated questions read as friction. Not a mood
              reading of any person.
            </p>
            <ul className="founders-sentiment">
              {["positive", "neutral", "friction"].map((key) => (
                <li key={key} className={`is-${key}`}>
                  <strong>{key}</strong>
                  <em>{snapshot.sentiment[key]}</em>
                  <i style={{ width: `${Math.round((snapshot.sentiment[key] / sentimentTotal) * 100)}%` }} />
                </li>
              ))}
            </ul>
          </section>

          <section className="founders-panel founders-panel--wide" aria-labelledby="fv-admin">
            <h2 id="fv-admin">Admin controls</h2>
            <div className="founders-admin">
              <div>
                <h3>Rooms</h3>
                <form
                  className="founders-mini"
                  onSubmit={(event) => {
                    event.preventDefault();
                    try {
                      addCustomHubRoom({ label: roomName, blurb: roomBlurb });
                      setRoomName("");
                      setRoomBlurb("");
                      flash("Room added to the hall.");
                    } catch (err) {
                      flash(err.message);
                    }
                  }}
                >
                  <input
                    className="field-input"
                    value={roomName}
                    onChange={(event) => setRoomName(event.target.value)}
                    placeholder="Room name"
                    required
                  />
                  <input
                    className="field-input"
                    value={roomBlurb}
                    onChange={(event) => setRoomBlurb(event.target.value)}
                    placeholder="A short note for the door"
                  />
                  <button className="primary-btn" type="submit">
                    Add room
                  </button>
                </form>
                <ul className="founders-room-list">
                  {snapshot.rooms.map((room) => (
                    <li key={room.id}>
                      <div>
                        <strong>{room.label}</strong>
                        <span>
                          {room.muted ? "Muted · " : ""}
                          {room.boosted ? "Boosted · " : ""}
                          {room.featured ? "Featured · " : ""}
                          {room.archived ? "Archived" : room.custom ? "Custom" : "Hall"}
                        </span>
                      </div>
                      {renameId === room.id ? (
                        <form
                          className="founders-mini"
                          onSubmit={(event) => {
                            event.preventDefault();
                            patchHubRoom(room.id, { label: renameLabel || room.label });
                            setRenameId("");
                            flash("Room renamed.");
                          }}
                        >
                          <input
                            className="field-input"
                            value={renameLabel}
                            onChange={(event) => setRenameLabel(event.target.value)}
                          />
                          <button className="ghost-btn" type="submit">
                            Save
                          </button>
                        </form>
                      ) : (
                        <div className="founders-tools">
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => {
                              patchHubRoom(room.id, { muted: !room.muted });
                              flash("Room mute updated.");
                            }}
                          >
                            {room.muted ? "Unmute" : "Mute"}
                          </button>
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => {
                              patchHubRoom(room.id, { boosted: !room.boosted });
                              flash("Boost updated.");
                            }}
                          >
                            {room.boosted ? "Unboost" : "Boost"}
                          </button>
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => {
                              patchHubRoom(room.id, { featured: !room.featured });
                              flash("Feature updated.");
                            }}
                          >
                            {room.featured ? "Unfeature" : "Feature"}
                          </button>
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => {
                              setRenameId(room.id);
                              setRenameLabel(room.label);
                            }}
                          >
                            Rename
                          </button>
                          {room.archived ? (
                            <button
                              type="button"
                              className="text-btn"
                              onClick={() => {
                                patchHubRoom(room.id, { archived: false, removed: false });
                                flash("Room restored.");
                              }}
                            >
                              Restore
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="text-btn"
                              onClick={() => {
                                try {
                                  removeHubRoom(room.id);
                                  flash("Room archived.");
                                } catch (err) {
                                  flash(err.message);
                                }
                              }}
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3>Listings</h3>
                <ul className="founders-event-admin">
                  {events.slice(0, 10).map((item) => (
                    <li key={item.id}>
                      <strong>{item.title}</strong>
                      <span>
                        {item.date}
                        {item.featured ? " · Featured" : ""}
                      </span>
                      <div className="founders-tools">
                        <button type="button" className="text-btn" onClick={() => setUrlEditItem(item)}>
                          Edit contact & URL
                        </button>
                        <button
                          type="button"
                          className="text-btn"
                          onClick={() => {
                            saveHubEvent({ ...item, featured: !item.featured }, session);
                            flash(item.featured ? "Listing returned to the regular row." : "Listing featured.");
                          }}
                        >
                          {item.featured ? "Unfeature" : "Feature"}
                        </button>
                        <button
                          type="button"
                          className="text-btn"
                          onClick={() => {
                            hideHubEvent(item.id, session);
                            flash("Listing removed.");
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <h3>Pending requests</h3>
                <ul className="founders-event-admin">
                  {requests
                    .filter((row) => row.status === "pending")
                    .map((item) => (
                      <li key={item.id}>
                        <strong>{item.title}</strong>
                        <span>
                          {item.from_name} · {item.mode}
                        </span>
                        <div className="founders-tools">
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => {
                              decideEventRequest(item.id, "approved", session);
                              flash("Approved.");
                            }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="text-btn"
                            onClick={() => {
                              decideEventRequest(item.id, "declined", session);
                              flash("Declined.");
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      </li>
                    ))}
                  {!requests.some((row) => row.status === "pending") ? <li className="muted">No pending requests.</li> : null}
                </ul>
              </div>

              <div>
                <h3>Member activity</h3>
                <label className="field">
                  <span className="field-label">Member</span>
                  <select
                    className="field-select"
                    value={watched?.id || ""}
                    onChange={(event) => setWatchUser(event.target.value)}
                  >
                    {authors.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name}
                      </option>
                    ))}
                  </select>
                </label>
                {watched ? (
                  <div className="founders-user">
                    <p>
                      {watched.name} · {watchedPosts.length} notes · last rooms:{" "}
                      {watchedRooms.map((row) => row.to).join(", ") || "none yet"}
                    </p>
                    <form
                      className="founders-mini"
                      onSubmit={(event) => {
                        event.preventDefault();
                        try {
                          warnHubMember(watched.id, warnNote);
                          setWarnNote("");
                          flash("A quiet warning was recorded.");
                        } catch (err) {
                          flash(err.message);
                        }
                      }}
                    >
                      <input
                        className="field-input"
                        value={warnNote}
                        onChange={(event) => setWarnNote(event.target.value)}
                        placeholder="A brief warning"
                      />
                      <button className="ghost-btn" type="submit">
                        Warn
                      </button>
                    </form>
                    {blocks.some((row) => row.user_id === watched.id) ? (
                      <button
                        type="button"
                        className="text-btn"
                        onClick={() => {
                          unblockHubUser(watched.id, session);
                          flash("Member restored.");
                        }}
                      >
                        Restore Hub access
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-btn"
                        onClick={() => {
                          try {
                            blockHubUser(watched.id, session);
                            flash("Member paused in The Hub.");
                          } catch (err) {
                            flash(err.message);
                          }
                        }}
                      >
                        Pause Hub access
                      </button>
                    )}
                    {warnings.filter((row) => row.user_id === watched.id).length ? (
                      <p className="muted">
                        Warnings: {warnings.filter((row) => row.user_id === watched.id).length}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="muted">No Hub members have written yet.</p>
                )}
              </div>

              <div>
                <h3>Listing sources</h3>
                <p className="muted">Curated in-system calendars. These are not live scrapes of outside sites.</p>
                <label className="field">
                  <span className="field-label">Auto-update every</span>
                  <select
                    className="field-select"
                    value={intervalHours}
                    onChange={(event) => {
                      const hours = Number(event.target.value);
                      setIntervalHours(hours);
                      setFounderSettings({ intervalHours: hours });
                      flash("Update rhythm saved.");
                    }}
                  >
                    <option value={6}>6 hours</option>
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours</option>
                    <option value={48}>48 hours</option>
                  </select>
                </label>
                <ul className="founders-sources">
                  {EVENT_SOURCES.map((source) => (
                    <li key={source.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={!offSources.has(source.id)}
                          onChange={() => toggleSource(source.id)}
                        />
                        {source.name}
                      </label>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => {
                    forceRefreshHubFeeds();
                    flash("Listings refreshed from the in-system catalog.");
                  }}
                >
                  Refresh listings now
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
      <HubListingEditModal
        item={urlEditItem}
        session={session}
        isOpen={Boolean(urlEditItem)}
        onClose={() => setUrlEditItem(null)}
        onSaved={() => flash("Event URL saved.")}
      />
    </AppLayout>
  );
}
